import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useCustomDomain } from '@/hooks/useCustomDomain';
import { buildSubdomain, APP_DOMAIN } from '@/lib/domain';
import {
  DNS_CNAME_TARGET,
  normalizeHostname,
  isApexDomain,
  resolveDomainState,
} from '@/lib/customDomain';

function cleanDisplayUrl(url: string): string {
  return url.replace(/^https?:\/\//, '');
}

const TONE_CLASSES: Record<string, string> = {
  neutral: 'bg-background-100 text-foreground-500',
  warn: 'bg-amber-100 text-amber-700',
  info: 'bg-primary-100 text-primary-700',
  success: 'bg-green-100 text-green-700',
  danger: 'bg-red-100 text-red-600',
};

/**
 * Section « Nom de domaine » du dashboard marchand.
 * Gère le sous-domaine Zifek + le domaine personnalisé via Cloudflare for SaaS.
 */
export default function DomainSettings() {
  const { user } = useAuth();
  const { loading, row, busy, error, message, connect, verify, disconnect, clearMessages } =
    useCustomDomain({ userId: user?.id, userEmail: user?.email });

  const [domainInput, setDomainInput] = useState('');
  const [copied, setCopied] = useState<null | 'subdomain' | 'target' | 'domain'>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const subdomain = user ? buildSubdomain(user.user_name) : '';

  // Pré-remplit le champ avec le domaine déjà enregistré
  useEffect(() => {
    if (row?.domaine) setDomainInput(row.domaine);
  }, [row?.domaine]);

  const copy = (value: string, key: 'subdomain' | 'target' | 'domain') => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    });
  };

  if (!user) return null;

  const hasDomain = !!row?.domaine;
  const status = resolveDomainState(row?.hostname_status, row?.ssl_status, !!row?.is_active);
  const isActive = !!row?.is_active;
  const normalizedInput = normalizeHostname(domainInput);
  const showApexHint = normalizedInput.length > 0 && isApexDomain(normalizedInput);
  const connecting = busy === 'connect';
  const verifying = busy === 'verify';
  const disconnecting = busy === 'disconnect';

  return (
    <div className="space-y-5">
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 md:p-6">
        <h3 className="text-sm font-semibold text-foreground-700 mb-1">Nom de domaine</h3>
        <p className="text-xs text-foreground-500 mb-5">
          Votre boutique est accessible via un sous-domaine Zifek gratuit. Ajoutez votre propre
          domaine pour une adresse 100% professionnelle.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-foreground-400 py-4">
            <i className="ri-loader-4-line animate-spin"></i>Chargement...
          </div>
        ) : (
          <div className="space-y-5">
            {/* ── Sous-domaine Zifek ── */}
            <div className="p-4 bg-background-100 rounded-lg">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-global-line"></i>Sous-domaine Zifek
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-medium">
                  Gratuit
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground-900 break-all">
                  {cleanDisplayUrl(subdomain)}
                </span>
                <button
                  onClick={() => copy(subdomain, 'subdomain')}
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-200/70 transition-colors cursor-pointer shrink-0"
                  title="Copier l'URL"
                >
                  <i className={`text-xs ${copied === 'subdomain' ? 'ri-check-line text-accent-500' : 'ri-file-copy-line text-foreground-400'}`}></i>
                </button>
              </div>
            </div>

            {/* ── Domaine personnalisé ── */}
            <div className="p-4 bg-background-100 rounded-lg">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <span className="text-xs font-medium text-foreground-500 flex items-center gap-1.5">
                  <i className="ri-link-m"></i>
                  {hasDomain ? 'Domaine personnalisé' : 'Ajouter un domaine personnalisé'}
                </span>
                {hasDomain && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${TONE_CLASSES[status.tone]}`}>
                    <i className={status.icon}></i>{status.label}
                  </span>
                )}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-foreground-400">https://</span>
                  <input
                    type="text"
                    value={domainInput}
                    onChange={(e) => { setDomainInput(e.target.value); clearMessages(); }}
                    placeholder="www.maboutique.com"
                    className="w-full pl-14 pr-3 py-2.5 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <button
                  onClick={() => connect(domainInput)}
                  disabled={connecting || !normalizedInput}
                  className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  {connecting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-plug-line"></i>}
                  <span>{hasDomain ? 'Reconnecter' : 'Connecter mon domaine'}</span>
                </button>
              </div>

              {showApexHint && (
                <p className="text-xs text-primary-700 mt-2 flex items-start gap-1.5">
                  <i className="ri-lightbulb-line mt-0.5"></i>
                  <span>
                    Nous recommandons <span className="font-semibold">www.{normalizedInput}</span> plutôt que{' '}
                    <span className="font-mono">{normalizedInput}</span> — c&apos;est plus compatible avec
                    la majorité des fournisseurs DNS. Vous pouvez aussi utiliser
                    <span className="font-mono"> shop.{normalizedInput}</span>.
                  </span>
                </p>
              )}

              {error && (
                <p className="text-xs text-red-500 mt-2 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>{error}
                </p>
              )}
              {message && (
                <p className="text-xs text-accent-600 mt-2 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i>{message}
                </p>
              )}

              {/* ── Instructions DNS + vérification ── */}
              {hasDomain && (
                <>
                  <div className="mt-4 p-3 rounded-lg bg-accent-50 border border-accent-100">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-guide-line text-accent-600 text-sm"></i>
                      <span className="text-xs font-semibold text-accent-700">Comment lier votre domaine</span>
                    </div>
                    <p className="text-xs text-foreground-600 mb-3">
                      Chez votre fournisseur DNS (OVH, GoDaddy, Namecheap…), créez cet
                      enregistrement. Aucun compte Cloudflare n&apos;est nécessaire.
                    </p>

                    <div className="flex items-center gap-3 bg-background-50 rounded-md px-3 py-2.5 border border-background-200/70">
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-foreground-400 uppercase tracking-wide mb-0.5">Type</div>
                        <div className="text-xs font-mono font-semibold text-foreground-900">CNAME</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-foreground-400 uppercase tracking-wide mb-0.5">Nom / Hôte</div>
                        <div className="text-xs font-mono text-foreground-900 break-all">
                          {normalizedInput.startsWith('www.') ? 'www' : normalizedInput.split('.')[0] || 'www'}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] text-foreground-400 uppercase tracking-wide mb-0.5">Cible / Valeur</div>
                        <div className="text-xs font-mono text-foreground-900 break-all">{DNS_CNAME_TARGET}</div>
                      </div>
                      <button
                        onClick={() => copy(DNS_CNAME_TARGET, 'target')}
                        className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-200/70 transition-colors cursor-pointer shrink-0"
                        title="Copier la cible DNS"
                      >
                        <i className={`text-sm ${copied === 'target' ? 'ri-check-line text-accent-500' : 'ri-file-copy-line text-foreground-400'}`}></i>
                      </button>
                    </div>
                    <p className="text-[11px] text-foreground-500 mt-3 leading-relaxed">
                      <i className="ri-information-line mr-1"></i>
                      Le SSL (HTTPS) est généré et validé automatiquement par Zifek. La propagation
                      DNS peut prendre de quelques minutes à quelques heures.
                    </p>
                  </div>

                  {/* Statut détaillé */}
                  <div className={`mt-4 p-3 rounded-lg ${
                    isActive ? 'bg-green-50' : status.tone === 'danger' ? 'bg-red-50' : 'bg-background-200/50'
                  }`}>
                    <div className="flex items-start gap-2">
                      <i className={`${status.icon} text-sm mt-0.5 ${
                        isActive ? 'text-green-600' : status.tone === 'danger' ? 'text-red-600' : 'text-foreground-500'
                      }`}></i>
                      <div className="flex-1">
                        {isActive ? (
                          <>
                            <p className="text-xs font-semibold text-green-700 mb-0.5">Domaine connecté</p>
                            <p className="text-xs text-green-600">Votre boutique est accessible sur votre domaine personnalisé, avec HTTPS actif.</p>
                          </>
                        ) : (
                          <>
                            <p className="text-xs font-semibold text-foreground-700 mb-0.5">{status.label}</p>
                            <p className="text-xs text-foreground-500">
                              {status.state === 'pending_dns' && 'En attente de la configuration DNS. Créez le CNAME indiqué ci-dessus puis lancez la vérification.'}
                              {status.state === 'ssl_pending' && 'Le certificat SSL est en cours de génération. Cela peut prendre quelques minutes.'}
                              {status.state === 'verifying' && 'Vérification en cours auprès de Cloudflare.'}
                              {status.state === 'error' && (row?.error_message || 'Une erreur est survenue. Vérifiez votre configuration DNS puis réessayez.')}
                              {status.state === 'not_configured' && 'Lancez la vérification pour connaître le statut.'}
                            </p>
                          </>
                        )}
                        <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-foreground-400">
                          <span>Hostname : <span className="font-mono">{row?.hostname_status || '—'}</span></span>
                          <span>SSL : <span className="font-mono">{row?.ssl_status || '—'}</span></span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <button
                      onClick={verify}
                      disabled={verifying}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-background-50 border border-background-200/70 text-foreground-600 hover:text-foreground-800 hover:border-background-300/60 transition-colors cursor-pointer disabled:opacity-50"
                    >
                      {verifying ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-refresh-line"></i>}
                      Vérifier la configuration
                    </button>

                    {isActive && row?.domaine && (
                      <a
                        href={`https://${row.domaine}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-external-link-line"></i>Ouvrir ma boutique
                      </a>
                    )}

                    {!confirmDisconnect ? (
                      <button
                        onClick={() => setConfirmDisconnect(true)}
                        disabled={disconnecting}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap text-foreground-400 hover:text-red-500 transition-colors cursor-pointer disabled:opacity-50"
                      >
                        <i className="ri-link-unlink-m"></i>Déconnecter le domaine
                      </button>
                    ) : (
                      <span className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-200">
                        <span className="text-xs text-red-600">Confirmer la déconnexion ?</span>
                        <button
                          onClick={async () => { await disconnect(); setConfirmDisconnect(false); setDomainInput(''); }}
                          disabled={disconnecting}
                          className="text-xs font-semibold text-red-600 hover:text-red-700 cursor-pointer disabled:opacity-50"
                        >
                          {disconnecting ? 'Suppression…' : 'Oui, déconnecter'}
                        </button>
                        <button
                          onClick={() => setConfirmDisconnect(false)}
                          className="text-xs text-foreground-400 hover:text-foreground-600 cursor-pointer"
                        >
                          Annuler
                        </button>
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-2">
                    <i className="ri-shield-check-line mr-1"></i>
                    Déconnecter le domaine ne supprime jamais votre compte ni votre sous-domaine
                    <span className="font-mono"> {user.user_name}.{APP_DOMAIN}</span>.
                  </p>
                </>
              )}
            </div>

            {/* ── Aperçu ── */}
            <div className="p-4 bg-background-100 rounded-lg">
              <span className="text-xs font-medium text-foreground-500 flex items-center gap-1.5 mb-2">
                <i className="ri-eye-line"></i>Aperçu
              </span>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-sm font-medium text-foreground-900 break-all">
                  {isActive && row?.domaine ? `https://${row.domaine}` : cleanDisplayUrl(subdomain)}
                </span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isActive ? 'bg-accent-100 text-accent-700' : 'bg-background-200/70 text-foreground-500'}`}>
                  {isActive ? 'Domaine perso actif' : 'Sous-domaine'}
                </span>
              </div>
              <p className="text-xs text-foreground-400 mt-2">
                {isActive
                  ? 'Votre boutique s\u2019affiche à l\u2019identique sur votre sous-domaine ET votre domaine personnalisé.'
                  : 'Votre boutique est accessible via votre sous-domaine Zifek gratuit.'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}